import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { horseAPI } from '../../../services/api';

const VICES = [
  { field: 'vices_cribber',       label: 'Cribber' },
  { field: 'vices_weaving',       label: 'Weaving' },
  { field: 'vices_stall_walking', label: 'Stall-Walking' },
  { field: 'vices_pawing',        label: 'Pawing' },
  { field: 'vices_wood_chewing',  label: 'Wood-Chewing' },
  { field: 'vices_wall_kicking',  label: 'Wall-Kicking' },
];
const DISCLAIMERS = [
  { field: 'other_issues_eye_certification', label: 'Eye Certification' },
  { field: 'other_issues_surgery',           label: 'Surgery' },
  { field: 'other_issues_regumate',          label: 'Regumate' },
  { field: 'other_issues_shoes_for_sale',    label: 'Shoes for the Sale' },
];

const HorseVicesCard = ({ horse, canEditAccount, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const startEditing = () => {
    setForm({
      vices_cribber: horse.vices_cribber || false,
      vices_weaving: horse.vices_weaving || false,
      vices_stall_walking: horse.vices_stall_walking || false,
      vices_pawing: horse.vices_pawing || false,
      vices_wood_chewing: horse.vices_wood_chewing || false,
      vices_wall_kicking: horse.vices_wall_kicking || false,
      other_issues_eye_certification: horse.other_issues_eye_certification || false,
      other_issues_surgery: horse.other_issues_surgery || false,
      other_issues_regumate: horse.other_issues_regumate || false,
      other_issues_shoes_for_sale: horse.other_issues_shoes_for_sale || false,
      vices_issues_notes: horse.vices_issues_notes || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await horseAPI.update(horse.id, form);
      toast.success('Vices & Disclaimers updated');
      setIsEditing(false);
      onUpdate?.();
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (field) => setForm(prev => ({ ...prev, [field]: !prev[field] }));

  const hasAnyVice = VICES.some(v => horse[v.field]);
  const hasAnyDisclaimer = DISCLAIMERS.some(d => horse[d.field]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">Vices &amp; Disclaimers</CardTitle>
        {canEditAccount && !isEditing && (
          <Button variant="ghost" size="icon" onClick={startEditing} data-testid="edit-vices-btn">
            <Pencil className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium mb-3 block">Vices (Behavioral)</label>
                <div className="space-y-2">
                  {VICES.map(({ field, label }) => (
                    <label key={field} className="flex items-center gap-2">
                      <input type="checkbox" checked={!!form[field]} onChange={() => toggle(field)} className="rounded accent-primary" />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-3 block">Other Disclaimers</label>
                <div className="space-y-2">
                  {DISCLAIMERS.map(({ field, label }) => (
                    <label key={field} className="flex items-center gap-2">
                      <input type="checkbox" checked={!!form[field]} onChange={() => toggle(field)} className="rounded accent-primary" />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-2 border-t pt-4">
              <label className="text-sm font-medium">Other Notes</label>
              <Textarea
                value={form.vices_issues_notes || ''}
                onChange={e => setForm(prev => ({ ...prev, vices_issues_notes: e.target.value }))}
                placeholder="Enter any additional notes..."
                rows={4}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            {hasAnyVice && (
              <div>
                <p className="font-medium mb-2">Vices (Behavioral):</p>
                <div className="flex flex-wrap gap-2">
                  {VICES.filter(v => horse[v.field]).map(v => (
                    <Badge key={v.field} variant="outline">{v.label}</Badge>
                  ))}
                </div>
              </div>
            )}
            {hasAnyDisclaimer && (
              <div>
                <p className="font-medium mb-2">Other Disclaimers:</p>
                <div className="flex flex-wrap gap-2">
                  {DISCLAIMERS.filter(d => horse[d.field]).map(d => (
                    <Badge key={d.field} variant="outline">{d.label}</Badge>
                  ))}
                </div>
              </div>
            )}
            {horse.vices_issues_notes && (
              <div className="border-t pt-3">
                <p className="font-medium mb-1">Other Notes:</p>
                <p className="whitespace-pre-wrap text-muted-foreground">{horse.vices_issues_notes}</p>
              </div>
            )}
            {!hasAnyVice && !hasAnyDisclaimer && !horse.vices_issues_notes && (
              <p className="text-muted-foreground italic">No vices or issues noted</p>
            )}
          </div>
        )}
        {isEditing && (
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HorseVicesCard;
